import app from "flarum/common/app";
import Component from "flarum/common/Component";
import Button from "flarum/common/components/Button";
import LoadingIndicator from "flarum/common/components/LoadingIndicator";
import humanTime from 'flarum/common/helpers/humanTime';
import { comment, expired, listing, permitted, request, shareUrl, single, t } from "../api";
import FactEditor from "./FactEditor";
export default class FactDetail extends Component {
  comments = [];
  next = null;
  commentText = "";
  busy = false;
  failed = false;
  gone = false;
  removed = false;
  share = false;
  viewed = false;
  oninit(vnode) {
    super.oninit(vnode);
    void this.load();
  }
  armExpiry() {
    if (!this.story) return;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.gone = true;
      this.attrs.onchanged?.();
      m.redraw();
    }, Math.max(0, Date.parse(this.story.expiresAt) - Date.now()));
  }
  onremove(vnode) {
    this.removed = true;
    clearTimeout(this.timer);
    super.onremove(vnode);
  }
  async load() {
    this.failed = false;
    try {
      const story = single(await request("/" + this.attrs.id));
      if (this.removed) return;
      this.story = story;
      this.armExpiry();
      await this.loadComments();
      if (!this.viewed && story.status === "approved" && !this.removed) {
        this.viewed = true;
        const result = single(await request("/" + story.id + "/view", "POST"));
        if (!this.removed && this.story) this.story.counts.views = result.counts.views;
      }
    } catch {
      this.failed = true;
    } finally {
      if (!this.removed) m.redraw();
    }
  }
  async loadComments(more) {
    if (more === void 0) {
      more = false;
    }
    const page = listing(await request("/" + this.attrs.id + "/comments" + (more && this.next ? "?before=" + this.next : "")), comment);
    if (this.removed) return;
    this.comments = more ? [...this.comments, ...page.data] : page.data;
    this.next = page.next;
  }
  async act(action) {
    if (this.busy) return;
    this.busy = true;
    try {
      await action();
      this.attrs.onchanged?.();
    } catch {
      /* Flarum displays API errors. */
    } finally {
      this.busy = false;
      if (!this.removed) m.redraw();
    }
  }
  view() {
    if (this.gone || this.story && expired(this.story)) return m("p", {
      className: "FactDetail-unavailable",
      role: "status"
    }, t("unavailable"));
    if (this.failed) return m("div", {
      role: "alert"
    }, t("unavailable"), " ", m(Button, {
      className: "Button",
      onclick: () => this.load()
    }, t("retry")));
    const story = this.story;
    if (!story) return m(LoadingIndicator, null);
    const content = story.author.name.charAt(0).toUpperCase();
    return m("article", {
      className: "FactDetail"
    }, m("header", {
      className: "FactDetail-header"
    }, story.author.avatar && m("img", {
      className: "Avatar",
      src: story.author.avatar,
      alt: ""
    }), !story.author.avatar && m("span", {
      className: "Username"
    }, content), m("strong", null, story.author.name), " ", m("span", null, t(story.status)), m("small", null, m("time", {
      datetime: story.createdAt
    }, humanTime(new Date(story.createdAt))), " ", "\xB7 ", t("views"), " ", story.counts.views, " \xB7 ", t("expires"), " ", new Date(story.expiresAt).toLocaleString())), m("div", {
      className: "FactDetail-media Fact-gradient--" + story.background
    }, story.images.map((image, i) => m("img", {
      key: image.path,
      src: image.url,
      alt: story.author.name + " · " + (i + 1),
      loading: "lazy"
    })), story.content && m("p", null, story.content)), m("div", {
      className: "FactDetail-actions"
    }, m(Button, {
      className: "Button " + (story.liked ? "Button--primary" : ""),
      icon: "far fa-thumbs-up",
      "aria-pressed": story.liked,
      disabled: this.busy || !permitted("canInteract") || story.status !== "approved",
      onclick: () => this.act(async () => {
        this.story = single(await request("/" + story.id + "/like", story.liked ? "DELETE" : "POST"));
      })
    }, t("like"), " ", story.counts.likes), m("span", null, t("comments"), " ", story.counts.comments), story.status === "approved" && m(Button, {
      className: "Button",
      icon: "fas fa-share-alt",
      onclick: async () => {
        const url = shareUrl(story);
        try {
          if (navigator.share) await navigator.share({
            url
          });else {
            this.share = true;
            m.redraw();
          }
        } catch {
          this.share = true;
          m.redraw();
        }
      }
    }, t("share")), story.canEdit && m(Button, {
      className: "Button",
      disabled: this.busy,
      onclick: () => app.modal.show(FactEditor, {
        story,
        onsaved: updated => {
          this.story = updated;
          this.attrs.onchanged?.();
        }
      })
    }, t("edit")), story.canDelete && m(Button, {
      className: "Button Button--danger",
      disabled: this.busy,
      onclick: () => {
        if (confirm(String(t("confirm_delete")))) void this.act(async () => {
          await request("/" + story.id, "DELETE");
          this.gone = true;
        });
      }
    }, t("delete")), story.canModerate && story.status !== "approved" && m(Button, {
      className: "Button Button--primary",
      disabled: this.busy,
      onclick: () => this.moderate("approved")
    }, t("approve")), story.canModerate && story.status !== "rejected" && m(Button, {
      className: "Button",
      disabled: this.busy,
      onclick: () => this.moderate("rejected")
    }, t("reject"))), this.share && m("label", {
      className: "FactDetail-share"
    }, t("share_link"), m("input", {
      className: "FormControl",
      readonly: true,
      value: shareUrl(story),
      onclick: e => e.target.select()
    })), !app.session.user && m("p", null, t("login_help")), story.status === "approved" && permitted("canInteract") && m("form", {
      onsubmit: e => {
        e.preventDefault();
        void this.act(async () => {
          await request("/" + story.id + "/comments", "POST", {
            content: this.commentText
          });
          this.commentText = "";
          await this.load();
        });
      }
    }, m("label", null, t("comment"), m("textarea", {
      className: "FormControl",
      maxlength: 1000,
      value: this.commentText,
      disabled: this.busy,
      oninput: e => this.commentText = e.target.value
    })), m(Button, {
      type: "submit",
      className: "Button",
      disabled: this.busy || !this.commentText.trim()
    }, t("send"))), m("ul", {
      className: "FactDetail-comments"
    }, this.comments.map(c => m("li", {
      key: c.id
    }, m("strong", null, c.author.name), m("small", null, " ", new Date(c.createdAt).toLocaleString()), m("p", null, c.content), c.canDelete && m(Button, {
      className: "Button Button--text",
      disabled: this.busy,
      onclick: () => this.act(async () => {
        await request("/" + story.id + "/comments/" + c.id, "DELETE");
        await this.load();
      })
    }, t("delete"))))), this.next && m(Button, {
      className: "Button",
      disabled: this.busy,
      onclick: () => this.act(() => this.loadComments(true))
    }, t("more")));
  }
  moderate(status) {
    const story = this.story;
    if (story) void this.act(async () => {
      this.story = single(await request("/" + story.id + "/moderation", "PATCH", {
        status,
        revision: story.revision
      }));
    });
  }
}
flarum.reg.add('xgay-fact', 'common/components/FactDetail', FactDetail);